import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import Keycloak from 'keycloak-js';
import {
  createInterceptorCondition,
  IncludeBearerTokenCondition,
} from 'keycloak-angular';
import { environment } from '../../../environments/environment';
import { KeycloakInitStateService } from '../services/keycloak-init-state.service';
import { apiBaseUrl } from './api.config';
import {
  clearKeycloakTokens,
  readStoredKeycloakTokens,
} from './keycloak-session.storage';

export const keycloakConfig = environment.keycloak;

/**
 * `check-sso` silenzioso: all'avvio l'app chiede a Keycloak, in un iframe
 * nascosto e con `prompt=none`, se esiste già una sessione sul realm. È così
 * che il login fatto in un altro client della suite (stesso realm, stesso
 * cookie SSO) viene riconosciuto appena si apre l'ecommerce, senza clic. Se la
 * sessione non c'è Keycloak risponde `login_required`, la libreria conclude
 * l'init da anonimi e vetrina, carrello e checkout restano liberi: nessun
 * redirect e nessuna pagina di login imposta.
 *
 * Il controllo funziona perché con `prompt=none` l'endpoint di autorizzazione
 * non rende mai la pagina di login (quella sì protetta da
 * `frame-ancestors 'self'`): risponde con un 302 verso
 * `silent-check-sso.html`, una pagina statica di questa origine che rimanda
 * l'esito alla finestra principale. Anche la pagina di verifica dei cookie di
 * terze parti del realm è incorniciabile (verificato il 21/09/2026). Il
 * tentativo del 15/09 era fallito perché la pagina statica non esisteva: il
 * dev server rispondeva con index.html e l'app si riavviava dentro l'iframe.
 *
 * `silentCheckSsoFallback: false`: se il browser blocca i cookie di terze
 * parti (Safari con il frontend su un dominio diverso da quello di Keycloak),
 * la libreria non deve ripiegare su un redirect a pagina intera, che
 * riporterebbe ogni visitatore anonimo su Keycloak e indietro con
 * `error=login_required`, facendo scattare l'avviso di accesso fallito. In
 * quel caso si parte anonimi e resta il click su "Accedi".
 *
 * I token conservati per la durata della scheda restano: al ricaricamento la
 * libreria li rinnova con una chiamata al token endpoint, più rapida
 * dell'iframe, e solo se il rinnovo fallisce passa al `check-sso`.
 *
 * `checkLoginIframe` resta disattivato: il polling periodico sullo stato della
 * sessione non serve e in alcuni browser produce solo errori in console.
 *
 * Non passare `redirectUri` qui: verrebbe valutato al caricamento del modulo,
 * fissandolo all'URL corrente (parametri OAuth `code`/`state` di ritorno
 * inclusi) e mandando il flusso in loop sulla pagina di login. Il redirect di
 * destinazione si passa alla chiamata `login()`, non all'init.
 */
export const keycloakInitOptions = {
  onLoad: 'check-sso' as const,
  silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
  silentCheckSsoFallback: false,
  checkLoginIframe: false,
  // Il client è pubblico: PKCE è l'unica protezione dello scambio del code.
  pkceMethod: 'S256' as const,
  // Letti al caricamento del modulo, che precede l'inizializzazione. Assenti
  // al primo accesso e dopo il logout.
  ...(readStoredKeycloakTokens() ?? {}),
};

/**
 * Vero se l'indirizzo è il ritorno da Keycloak: `state` insieme a `code` (o a
 * `error`), nel frammento con `response_mode=fragment`, nella query altrimenti.
 */
export function hasKeycloakCallback(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  for (const raw of [parsed.hash.replace(/^#/, ''), parsed.search.replace(/^\?/, '')]) {
    const params = new URLSearchParams(raw);
    if (params.has('state') && (params.has('code') || params.has('error'))) {
      return true;
    }
  }
  return false;
}

/**
 * Inizializzazione Keycloak gestita dall'app anziché da `provideKeycloak`.
 *
 * L'initializer della libreria intercetta l'errore di `init()` e si limita a
 * scriverlo in console, così l'app parte anonima senza che nessuno sappia che
 * il ritorno da Keycloak è fallito; i guard rilanciano il login e, con il
 * cookie di sessione già presente sul realm, il flusso gira in loop. Qui
 * l'esito viene registrato in `KeycloakInitStateService`: se l'indirizzo era
 * un ritorno da Keycloak e l'utente resta anonimo (errore nello scambio del
 * `code`, oppure callback scartato in silenzio perché lo `state` non è più in
 * localStorage), i guard non riavviano il login da soli.
 *
 * Il fallimento senza callback (token conservati nella scheda non più
 * rinnovabili) è normale, ad esempio dopo la scadenza della sessione lato
 * server: si ripulisce la copia locale e si resta anonimi senza avvisi.
 */
export function provideKeycloakInit(): EnvironmentProviders {
  return provideAppInitializer(async () => {
    const keycloak = inject(Keycloak);
    const initState = inject(KeycloakInitStateService);
    const returningFromKeycloak = hasKeycloakCallback(window.location.href);

    try {
      await keycloak.init(keycloakInitOptions);
    } catch (error) {
      clearKeycloakTokens();
      console.error('Inizializzazione Keycloak fallita', error);
    }

    if (returningFromKeycloak && !keycloak.authenticated) {
      initState.markLoginFailed();
    }
  });
}

/**
 * Il token Keycloak va allegato sia alle API gestionali (`/v1.0/...`) sia a
 * quelle ecommerce (`/api/v1/ecommerce/...`): dal 15/09/2026 il backend valida
 * il JWT Keycloak anche sull'ecommerce (`GET /auth/config` → `mode: keycloak`)
 * e, al primo Bearer di un utente mai visto, crea da sé utente portale e
 * partner locali (binding `linked_existing`).
 *
 * L'interceptor di keycloak-angular aggiunge l'header solo se l'utente è
 * autenticato su Keycloak e prima rinfresca il token se in scadenza. Se non lo
 * aggiunge, `ecommerceBearerInterceptor` ripiega sulla sessione legacy
 * (`POST /auth/login`), che resta valida solo con `mode: legacy`.
 */
export const apiBearerCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: new RegExp(
    `^${apiBaseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\/(v1\\.0|api\\/v1\\/ecommerce)(\\/.*)?$`,
    'i'
  ),
  bearerPrefix: 'Bearer',
});
